import React, { useState } from 'react';
import { TextField, InputAdornment, TablePagination, Box, Typography, Button, Tooltip, Chip, Paper } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useGetCandidateScheduledList } from '../../hooks/useCandidate';
import { TH, TD, Row, TableWrap, TABLE_STYLE } from '../common/tableStyles';

// ---- FORTHEYE soft-card tokens (shared with the rest of the portal) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';

// Friendly "current stage" label for any active candidate — handles freshly
// scheduled (no rounds yet) and selected/joined cases, not just in-round ones.
const getStage = (applicant) => {
  if (applicant.final_status === 'joined') return 'Joined';
  if (applicant.final_status === 'pending') return 'Onboarding';
  const statuses = applicant.statuses || [];
  if (statuses.some((s) => s.status === 'selected')) return 'Selected';
  const rounds = statuses.filter((s) => s.status === 'move_to_next_round');
  if (rounds.length) {
    const latest = [...rounds].sort((a, b) => b.id - a.id)[0];
    const stage = latest.next_round
      ? latest.next_round.replace(/_/g, ' ').toUpperCase()
      : 'NEXT ROUND';
    return `${stage} (R${latest.round_number})`;
  }
  return 'Scheduled';
};

const ProgressCand = () => {
  const { data: sheduledlist } = useGetCandidateScheduledList();

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch data from API
  // useEffect(() => {
  //   const fetchData = async () => {
  //     try {
  //       const response = await fetch('http://127.0.0.1:8000/applications/');
  //       const data = await response.json();
  //       // console.log(data);
  //       // Filter only rejected applicants
  //       const rejectedApplicants = data.filter((applicant) =>
  //         applicant.statuses.some(
  //           (status) => status.status === 'move_to_next_round'
  //         )
  //       );
  //       setApplicantsData(rejectedApplicants);
  //     } catch (error) {
  //       console.error('Error fetching the data:', error);
  //     }
  //   };

  //   fetchData();
  // }, []);



  const filteredCandidates = Array.isArray(sheduledlist)
    ? sheduledlist
      // Full active pipeline: scheduled -> rounds -> selected -> joined.
      // Exclude only those rejected at interview or dropped / did not join.
      .filter(
        (applicant) =>
          !(applicant.statuses || []).some((status) => status.status === 'rejected') &&
          applicant.final_status !== 'rejected'
      )
      .filter((applicant) =>
        applicant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        applicant.email_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        applicant.phone_number?.includes(searchTerm) ||
        applicant.role?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  const paginatedCandidates = filteredCandidates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header card */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <TimelineIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
              On-Going Interview Progress
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Track candidates moving through the active interview pipeline.
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Filter / search card */}
      <Paper elevation={0} sx={{ ...CARD_SX, p: { xs: 1.75, sm: 2 }, mb: { xs: 2, md: 3 }, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by Name, Email, Phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', sm: '320px' }, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8fafc' } }}
        />
      </Paper>

      {/* Table card */}
      <Paper elevation={0} sx={CARD_SX}>
        {filteredCandidates.length === 0 ? (
          <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5 }}>
            <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TimelineIcon />
            </Box>
            <Typography sx={{ fontWeight: 700, color: '#111827' }}>No On-Going Candidates Found</Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>Try adjusting your search or check back later.</Typography>
          </Box>
        ) : (
          <>
            <TableWrap>
              <table style={TABLE_STYLE}>
                <thead>
                  <tr>
                    <th style={TH}>S.No</th>
                    <th style={TH}>Name</th>
                    <th style={TH}>Role</th>
                    <th style={TH}>Current Stage</th>
                    <th style={TH}>Round History</th>
                    <th style={TH}>Contact No</th>
                    <th style={TH}>Resume</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCandidates.map((applicant, index) => {
                    // Sort statuses newest-first; may be empty for freshly scheduled.
                    const sortedStatuses = [...(applicant.statuses || [])].sort((a, b) => b.id - a.id);

                    return (
                      <Row key={index}>
                        <td style={TD}>{page * rowsPerPage + index + 1}</td>
                        <td style={{ ...TD, fontWeight: 600 }}>{applicant.name || 'N/A'}</td>
                        <td style={TD}>{applicant.role || 'N/A'}</td>
                        <td style={TD}>
                          <Chip
                            label={getStage(applicant)}
                            size="small"
                            sx={{ fontWeight: 700, border: 'none', bgcolor: '#eef4ff', color: ACCENT, borderRadius: '8px' }}
                          />
                        </td>
                        <td style={TD}>
                          <Tooltip
                            title={
                              <Box sx={{ p: 1 }}>
                                {sortedStatuses.length ? (
                                  sortedStatuses.map((s, idx) => (
                                    <Box key={idx} sx={{ borderBottom: '1px solid #555', py: 0.5 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                                        Round {s.round_number}: {s.status.replace(/_/g, ' ')}
                                      </Typography>
                                      <Typography variant="caption">
                                        {s.next_round && `Stage: ${s.next_round} | `}
                                        Date: {s.interview_date || 'N/A'}
                                      </Typography>
                                    </Box>
                                  ))
                                ) : (
                                  <Typography variant="caption">Scheduled — no rounds yet</Typography>
                                )}
                              </Box>
                            }
                            arrow
                          >
                            <Button
                              size="small"
                              startIcon={<HistoryIcon />}
                              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: ACCENT, bgcolor: `${ACCENT}14`, '&:hover': { bgcolor: `${ACCENT}22` } }}
                            >
                              View History
                            </Button>
                          </Tooltip>
                        </td>
                        <td style={TD}>{applicant.phone_number || 'N/A'}</td>
                        <td style={TD}>
                          {applicant.resume ? (
                            <a href={applicant.resume} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: ACCENT, fontWeight: 600 }}>
                              Resume
                            </a>
                          ) : 'N/A'}
                        </td>
                      </Row>
                    );
                  })}
                </tbody>
              </table>
            </TableWrap>

            <TablePagination
              component="div"
              count={filteredCandidates.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ProgressCand;
